import {
  CREATED_BY_CLOUD_SHELL,
  MANAGED_BY_TERRAFORM,
  P0_TAG_NAME,
} from "@p0security/integrations-aws-shared/constants";
import {
  buildGcpP0TrustPolicy,
  envSuffixFor,
} from "@p0security/integrations-aws-shared/policy/shared";
import type { DevEnv } from "@p0security/shared/types/environment";

import type { SqsQueueArn } from "./arn.ts";

export const sqsRoleName = (context: DevEnv): string =>
  `P0RoleIamEventSender${envSuffixFor(context)}`;

export const sqsInlinePolicyName = (context: DevEnv): string =>
  `${sqsRoleName(context)}Policy`;

export const sqsInlinePolicy = (
  context: DevEnv,
  args: { queue: SqsQueueArn; queueArn: string; kmsKeyArn?: string }
) => {
  const { queue, queueArn, kmsKeyArn } = args;
  const roleArn = `arn:${queue.partition}:iam::${queue.accountId}:role/${sqsRoleName(context)}`;
  return {
    Version: "2012-10-17",
    Statement: [
      {
        Sid: "P0CanVerifyOwnRole",
        Effect: "Allow",
        Action: ["iam:GetRole", "iam:GetRolePolicy"],
        Resource: roleArn,
      },
      {
        Sid: "P0CanSendToQueue",
        Effect: "Allow",
        Action: "sqs:SendMessage",
        Resource: queueArn,
      },
      ...(kmsKeyArn
        ? [
            {
              Sid: "P0CanEncryptForQueue",
              Effect: "Allow",
              Action: ["kms:GenerateDataKey", "kms:Decrypt"],
              Resource: kmsKeyArn,
            },
          ]
        : []),
    ],
  };
};

type InstallArgs = {
  gcloudServiceAccountId: string;
  queue: SqsQueueArn;
  queueArn: string;
  kmsKeyArn?: string;
};

export const sqsInstallCommands = (
  context: DevEnv,
  args: InstallArgs
): string => {
  const roleName = sqsRoleName(context);
  const trustPolicy = buildGcpP0TrustPolicy(args.gcloudServiceAccountId);
  const policy = sqsInlinePolicy(context, args);
  return `export CALLER=$(aws sts get-caller-identity --query Arn --output text) && \\
(aws iam get-role --role-name "${roleName}" > /dev/null 2>&1 || \\
aws iam create-role \\
  --tags Key="${P0_TAG_NAME}",Value="${CREATED_BY_CLOUD_SHELL} by \${CALLER}" \\
  --role-name "${roleName}" \\
  --assume-role-policy-document '${JSON.stringify(trustPolicy, undefined, 2)}') \\
  && \\
aws iam put-role-policy \\
  --role-name "${roleName}" \\
  --policy-name "${sqsInlinePolicyName(context)}" \\
  --policy-document '${JSON.stringify(policy, undefined, 2)}'`;
};

export const sqsInstallTerraform = (
  context: DevEnv,
  args: InstallArgs
): string => {
  const roleName = sqsRoleName(context);
  const trustPolicy = buildGcpP0TrustPolicy(args.gcloudServiceAccountId);
  const policy = sqsInlinePolicy(context, args);
  return `resource "aws_iam_role" "p0_aws_sqs_event_sender_role" {
  name = "${roleName}"

  assume_role_policy = <<EOF
${JSON.stringify(trustPolicy, undefined, 2)}
EOF

  tags = {
    ${P0_TAG_NAME} = "${MANAGED_BY_TERRAFORM}"
  }
}

resource "aws_iam_role_policy" "p0_aws_sqs_event_sender_policy" {
  name = "${sqsInlinePolicyName(context)}"
  role = aws_iam_role.p0_aws_sqs_event_sender_role.name

  policy = <<EOF
${JSON.stringify(policy, undefined, 2)}
EOF
}`;
};
